import clsx from "clsx";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

interface Props {
  name: string;
  options: Array<{
    label: string;
    shortLabel?: string;
    value: string | number;
  }>;
  border?: boolean;
  className?: string;
  size?: "small" | "big" | "bigger";
  required?: boolean;
}

const Select = ({
  name,
  options,
  className,
  border,
  size = "small",
  required,
}: Props) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const value = useWatch({ name });

  const error = errors[name];

  const label = useMemo(() => {
    const active = options.find((o) =>
      value
        ? `${o.value}` === `${value}`
        : ["", "all"].includes(o.value as string),
    );
    return active?.shortLabel ?? active?.label;
  }, [options, value]);

  return (
    <div className={clsx("relative w-full", className)}>
      <span
        className={clsx(
          "absolute inline-flex items-center z-10 pointer-events-none",
          {
            "h-9 px-[0.625rem] text-14": size === "small",
            "h-12 px-3 text-14": size === "big",
            "h-14 px-5 text-16": size === "bigger",
          },
        )}
      >
        {label}
      </span>
      <select
        {...register(name, {
          required: required
            ? typeof required === "string"
              ? required
              : "이 항목은 필수입니다."
            : false,
        })}
        className={clsx(
          "select appearance-none bg-no-repeat w-full block rounded-md bg-white text-[transparent]",
          size,
          {
            "border border-secondary": border,
            "!border-red": error,
            "h-9 px-[0.625rem] text-14": size === "small",
            "h-12 px-3 text-14": size === "big",
            "h-14 px-5 border border-gray text-16": size === "bigger",
          },
        )}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default Select;
